import React from 'react';
import { func, node, oneOf, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { Button, Icon } from '@fiverr-private/fit';
import { MODES, THEMES, FILLS } from './constants';
import THEME_CONFIG from './themeConfig';

import classes from './index.module.scss';

/**
 * @deprecated Please use Alert from @fiverr-private/feedback instead.
 */
const Alert = ({
    className,
    text,
    title,
    actionText,
    actionFill,
    onActionClick,
    href,
    theme,
    mode
}) => {

    const shouldShowActionButton = actionText && (onActionClick || href);
    const shouldCenterIcon = !title && shouldShowActionButton;

    const {
        icon,
        actionButtonColor,
        themeStyles
    } = THEME_CONFIG[theme];

    const {
        iconStyle,
        themeStyle
    } = themeStyles[mode];

    const wrapperClasses = classNames(
        classes.container,
        themeStyle,
        {
            [classes.containerCenterIcon]: shouldCenterIcon
        },
        className
    );

    const iconClasses = classNames(
        classes.icon,
        iconStyle,
        {
            [classes.centerIcon]: shouldCenterIcon
        }
    );

    const innerContainerClasses = classNames(
        classes.innerContainer,
        {
            [classes.containerCenterIcon]: shouldCenterIcon
        }
    );

    const buttonProps = {
        className: classNames('flex', classes.actionButton),
        color: mode === MODES.LIGHT ? actionButtonColor : Button.COLORS.WHITE,
        textColor: actionButtonColor,
        fill: actionFill,
        onClick: onActionClick
    };

    if (href) {
        Object.assign(buttonProps, {
            href,
            target: '_blank'
        });
    }

    return (
        <div className={wrapperClasses} role="alert">
            <Icon size={16} className={iconClasses}>{icon}</Icon>
            <div className={innerContainerClasses}>
                <div>
                    {title && (
                        <h6 className={classNames('text-display-7', classes.title, themeStyle)}>
                            {title}
                        </h6>
                    )}
                    <p>{text}</p>
                </div>
                {shouldShowActionButton && (
                    <Button {...buttonProps}>
                        {actionText}
                    </Button>
                )}
            </div>
        </div>
    );
};

Alert.displayName = 'Alert';

Alert.propTypes = {
    /**
     * Custom classes you wish to pass to the component.
     */
    className: string,
    /**
     * The main text for the alert.
     */
    text: oneOfType([string, node]).isRequired,
    /**
     * An optional title to be rendered above the main text.
     */
    title: oneOfType([string, node]),
    /**
     * Text for the action button
     */
    actionText: oneOfType([string, node]),
    /**
     * The alert's action button's fill option. Supported options are exposed through the static `FILLS` property.
     */
    actionFill: oneOf(Object.values(FILLS)),
    /**
     * Click handler for the action button.
     */
    onActionClick: func,
    /**
     * URL the action button redirects to.
     */
    href: string,
    /**
     * The alert's theme. Supported options are exposed through the static `THEMES` property.
     */
    theme: oneOf(Object.values(THEMES)),
    /**
     * The alert's mode. Supported options are exposed through the static `MODES` property.
     */
    mode: oneOf(Object.values(MODES))
};

Alert.defaultProps = {
    theme: THEMES.INFORMATION,
    mode: MODES.LIGHT
};

Object.assign(Alert, {
    THEMES,
    MODES,
    FILLS
});

export default Alert;
